import type { ObservabilityContext } from '../types/observability.types';
import type { ObservabilityApp, ObservabilityEnvironment } from '../types/tenant.types';
import { resolveEnvironment } from '../utils/environment';
import { nowIso } from '../utils/time';
import { getCorrelationId, getRequestId } from './requestId';

type MutableFrontendContext = {
  app: ObservabilityApp;
  service: string;
  environment: ObservabilityEnvironment;
  version?: string;
  releaseId?: string;
  gitCommitSha?: string;
  deploymentId?: string;
  userId?: string;
  tenantId?: string;
  companyId?: string;
  role?: string;
  subscriptionPlan?: string;
  sessionId?: string;
};

const state: MutableFrontendContext = {
  app: 'tenant-app',
  service: 'frontend',
  environment: 'development',
};

function generateSessionId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function inferDeviceType(ua: string): string {
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  return 'desktop';
}

export function initFrontendContext(input: {
  app: ObservabilityApp;
  service: string;
  environment?: string;
  version?: string;
  releaseId?: string;
  gitCommitSha?: string;
  deploymentId?: string;
  sessionId?: string;
}): void {
  state.app = input.app;
  state.service = input.service;
  state.environment = resolveEnvironment(input.environment);
  state.version = input.version;
  state.releaseId = input.releaseId;
  state.gitCommitSha = input.gitCommitSha;
  state.deploymentId = input.deploymentId;
  state.sessionId = input.sessionId ?? state.sessionId ?? generateSessionId();
}

export function setUserContext(input: { userId: string; role?: string | null } | null): void {
  state.userId = input?.userId;
  state.role = input?.role ?? undefined;
}

export function setTenantContext(input: { tenantId?: string; companyId?: string } | null): void {
  state.tenantId = input?.tenantId;
  state.companyId = input?.companyId;
}

export function setReleaseContext(input: {
  version?: string;
  releaseId?: string;
  gitCommitSha?: string;
  deploymentId?: string;
}): void {
  state.version = input.version ?? state.version;
  state.releaseId = input.releaseId ?? state.releaseId;
  state.gitCommitSha = input.gitCommitSha ?? state.gitCommitSha;
  state.deploymentId = input.deploymentId ?? state.deploymentId;
}

export function clearUserContext(): void {
  state.userId = undefined;
  state.role = undefined;
  state.tenantId = undefined;
  state.companyId = undefined;
}

export function getFrontendContext(
  overrides: Partial<ObservabilityContext> = {},
): ObservabilityContext {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  const route = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const timezone =
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;

  return {
    timestamp: nowIso(),
    app: state.app,
    service: state.service,
    environment: state.environment,
    version: state.version,
    releaseId: state.releaseId,
    gitCommitSha: state.gitCommitSha,
    deploymentId: state.deploymentId,
    sessionId: state.sessionId,
    requestId: getRequestId(),
    correlationId: getCorrelationId() ?? undefined,
    userId: state.userId,
    tenantId: state.tenantId,
    companyId: state.companyId,
    role: state.role,
    subscriptionPlan: state.subscriptionPlan,
    timezone,
    route,
    userAgent,
    browser: userAgent?.split(' ')[0],
    deviceType: userAgent ? inferDeviceType(userAgent) : undefined,
    ...overrides,
  };
}
